import { useState } from "react";
import { Folder, ChevronRight, Eye } from "lucide-react";
import BDMAssetCard from "./BDMAssetCard";
import AssetThumb from "./AssetThumb";
import AssetLightbox from "./AssetLightbox";

const TIER_BADGE = {
  personal: { label: "Just for you", cls: "bg-lime text-navy" },
  officer: { label: "Your officer", cls: "bg-teal/15 text-teal border border-teal/30" },
  everyone: { label: "All partners", cls: "bg-linen text-ink-500 border border-linen-border" },
};

const STRIP = 5;

export const ShelfFolder = ({ folder, authHeader, onLog, loggedByAsset, onGrabbed, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [previewAt, setPreviewAt] = useState(null);
  const badge = TIER_BADGE[folder.tier] || TIER_BADGE.everyone;
  const assets = folder.assets || [];
  const strip = assets.slice(0, STRIP);
  const extra = assets.length - strip.length;

  const openPreview = (asset) => setPreviewAt(assets.findIndex((a) => a.id === asset.id));

  return (
    <section className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden" data-testid={`bdm-folder-${folder.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`bdm-folder-toggle-${folder.id}`}
        className="w-full text-left px-5 sm:px-6 py-4 flex items-start justify-between gap-3 hover:bg-linen/40"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Folder className="w-4 h-4 text-teal shrink-0" />
            <h3 className="font-heading font-medium text-lg text-navy truncate">{folder.name}</h3>
            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md ${badge.cls}`}>
              {folder.tier === "officer" && folder.officer_names ? folder.officer_names : badge.label}
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            {assets.length} item{assets.length === 1 ? "" : "s"}
            {folder.subcategory_name ? ` · ${folder.subcategory_name}` : ""}
          </p>
          {folder.shelf_note ? (
            <p className="mt-1.5 text-sm text-ink-500">{folder.shelf_note}</p>
          ) : folder.description && folder.description !== folder.name ? (
            <p className="mt-1.5 text-sm text-ink-500">{folder.description}</p>
          ) : null}
        </div>
        <ChevronRight className={`w-5 h-5 text-ink-400 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {/* Closed folders still show what's inside — you can't use what you can't see. */}
      {!open && strip.length > 0 && (
        <div className="px-5 sm:px-6 pb-4 flex items-center gap-2" data-testid={`bdm-folder-strip-${folder.id}`}>
          {strip.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => openPreview(a)}
              data-testid={`bdm-strip-thumb-${a.id}`}
              title={a.filename}
              aria-label={`Preview ${a.filename}`}
              className="relative w-16 h-16 rounded-xl overflow-hidden border border-linen-border bg-linen hover:border-teal transition-colors group shrink-0"
            >
              <AssetThumb asset={a} authHeader={authHeader} className="w-full h-full" iconSize={16} />
              <span className="absolute inset-0 bg-navy-deep/0 group-hover:bg-navy-deep/40 transition-colors flex items-center justify-center">
                <Eye size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </button>
          ))}
          {extra > 0 && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid={`bdm-folder-more-${folder.id}`}
              className="w-16 h-16 rounded-xl border border-dashed border-linen-border text-xs font-semibold text-ink-500 hover:border-teal hover:text-teal transition-colors shrink-0"
            >
              +{extra} more
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="px-5 sm:px-6 pb-6">
          {assets.length === 0 ? (
            <p className="text-sm text-ink-500 italic">Nothing in this folder yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {assets.map((a) => (
                <BDMAssetCard
                  key={a.id}
                  asset={a}
                  authHeader={authHeader}
                  logged={loggedByAsset[a.id] || 0}
                  onGrabbed={(asset) => onGrabbed?.(asset, folder)}
                  onLog={(asset) => onLog(asset, folder)}
                  onPreview={openPreview}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {previewAt !== null && previewAt >= 0 && (
        <AssetLightbox
          assets={assets}
          index={previewAt}
          authHeader={authHeader}
          onClose={() => setPreviewAt(null)}
          onGrabbed={(asset) => onGrabbed?.(asset, folder)}
          onLog={(asset) => {
            setPreviewAt(null);
            onLog(asset, folder);
          }}
        />
      )}
    </section>
  );
};

export default ShelfFolder;
